/**
 * UI State Atoms
 *
 * Simplified Jotai atoms for basic UI state management:
 * - Dashboard views and simple state
 * - Modal and sidebar states
 * - User preferences
 */

import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { DashboardView, ModalState, SidebarState, UIPreferences } from '@/lib/placeholder-types';

// Dashboard view state
export const dashboardViewAtom = atom<DashboardView>('MAP_VIEW');

// Map state for orthographic view (indoor maps use pixel coordinates).
// zoom/target defaults are placeholders — the TrackingMap component
// calculates fit-to-viewport values on mount based on floor plan dimensions.
export const mapStateAtom = atom({
  zoom: 0,
  target: [0, 0] as [number, number],
  minZoom: -2,
  maxZoom: 5,
  selectedLayers: ['ASSETS', 'ZONES'] as string[],
  showZones: true,
  showPaths: true,
  showLabels: true,
  trailMaxAgeMs: 15_000,
});

// Asset type filters — null means pass-through (show all), Set means explicit
// selection (including empty Set → show nothing). Consumers must null-check.
export const activeTypeFiltersAtom = atom<Set<string> | null>(null);

// Asset ID filters — same sentinel pattern as activeTypeFiltersAtom.
export const activeAssetFiltersAtom = atom<Set<string> | null>(null);

// Heatmap visualization toggle (separate from heatmap mode in analytics.atoms)
export const heatmapEnabledAtom = atom(false);

// Heatmap control atoms — time preset, radius, intensity
export type HeatmapTimePreset = '1h' | '8h' | '24h' | '7d' | '30d';
export const heatmapTimePresetAtom = atom<HeatmapTimePreset>('1h');
export const heatmapRadiusAtom = atom<number>(40);
export const heatmapIntensityAtom = atom<number>(1);

// Calibration mode — active when calibration panel is expanded
export const calibrationModeAtom = atom(false);

// Calibration drag mode — active when "Move" toggle is on in calibration panel
export const calibrationDragModeAtom = atom(false);

// Fit-to-view trigger — controls write Date.now(), map reads & resets view
export const fitToViewTriggerAtom = atom(0);

// Center-on-position trigger — asset panel writes target, map animates to it
// ts=0 means no pending center; ts>0 is a pending center request
export const centerOnPositionAtom = atom({ target: [0, 0] as [number, number], ts: 0 });

// Derived atom for selected layers
export const selectedLayersAtom = atom(get => {
  const mapState = get(mapStateAtom);
  return new Set(mapState.selectedLayers);
});

// Modal state management
export const modalStateAtom = atom<ModalState>({
  isOpen: false,
  type: null,
  size: 'medium',
  data: undefined,
});

// Modal state helpers
export const openModalAtom = atom(
  null,
  (
    _get,
    set,
    { type, data, size }: { type: ModalState['type']; data?: unknown; size?: ModalState['size'] }
  ): void => {
    set(modalStateAtom, {
      isOpen: true,
      type,
      data,
      size: size || 'medium',
    });
  }
);

export const closeModalAtom = atom(null, (_get, set): void => {
  set(modalStateAtom, {
    isOpen: false,
    type: null,
    size: 'medium',
    data: undefined,
  });
});

// Sidebar state management
export const sidebarStateAtom = atomWithStorage<SidebarState>('sidebar-state', {
  isOpen: true,
  activeTab: 'ASSETS',
  pinned: false,
  width: 320,
});

// Sidebar helpers
export const toggleSidebarAtom = atom(null, (get, set): void => {
  const current = get(sidebarStateAtom);
  set(sidebarStateAtom, { ...current, isOpen: !current.isOpen });
});

export const setSidebarTabAtom = atom(null, (get, set, tab: SidebarState['activeTab']): void => {
  const current = get(sidebarStateAtom);
  set(sidebarStateAtom, { ...current, activeTab: tab });
});

// User preferences (persisted)
export const uiPreferencesAtom = atomWithStorage<UIPreferences>('ui-preferences', {
  theme: 'system',
  density: 'standard',
  animations: true,
  sounds: true,
  keyboard: true,
  colorBlindMode: false,
  highContrast: false,
  fontSize: 'medium',
});

// Theme mode derived atom
export const themeAtom = atom(get => {
  const preferences = get(uiPreferencesAtom);
  if (preferences.theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return preferences.theme;
});

// Simple loading state
export const isLoadingAtom = atom<boolean>(false);

// Reset UI state helper
export const resetUIStateAtom = atom(null, (_get, set): void => {
  set(modalStateAtom, {
    isOpen: false,
    type: null,
    size: 'medium',
    data: undefined,
  });
});

// Keyboard shortcut state
export const keyboardShortcutsActiveAtom = atom(get => get(uiPreferencesAtom).keyboard);

// Accessibility settings
export const accessibilityAtom = atom(get => {
  const prefs = get(uiPreferencesAtom);
  return {
    colorBlindMode: prefs.colorBlindMode,
    highContrast: prefs.highContrast,
    fontSize: prefs.fontSize,
    animations: prefs.animations,
  };
});
